import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { configure } from "@testing-library/dom";
import { setSelectionRange } from "@testing-library/user-event/dist/utils";
import headerImg from "../assets/headerbanner.png";
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  increment,
} from "firebase/firestore";
import { db, auth } from "../firebase"; // adjust path
import { Modal } from "bootstrap";
import Watermark from "./configure/Watermark";

// import fscgovtlogo from "../assets/fsc-govt-card.png"; // Adjust the path as necessary
const FscCard = () => {
  const [fscNumber, setFscNumber] = useState("");
  const [cardDetails, setCardDetails] = useState(null);
  const [debugLog, setDebugLog] = useState("");
  const [memberNames, setMemberNames] = useState([]);
  const [downloadFormat, setDownloadFormat] = useState("png");
  const [showConfig, setShowConfig] = useState(false);
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [format, setFormat] = useState("png");
  const [savedSettings, setSavedSettings] = useState(null);
  const [setting, setSettings] = useState({
    width: "85.6",
    height: "53.98",
    format: "png",
  });
  const [showModal, setShowModal] = useState(false);
  const pendingDownloadData = useRef(null);
  const downloadRef = useRef(null);
  const modalRef = useRef(null);

  // Listen for data from the popup
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.fscData && Array.isArray(data.fscData)) {
          localStorage.setItem("fscData", JSON.stringify(data));
          console.log("✅ FSC data received from popup:", data);
        }
      } catch (e) {
        console.warn("⚠️ Invalid message data received:", event.data);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("cardSettings");
    if (stored) {
      setSavedSettings(JSON.parse(stored));
    }
  }, []);

  const [cardStyle, setCardStyle] = useState({
    width: "1050px",
    height: "660px",
  });

  useEffect(() => {
    const settings = localStorage.getItem("cardSettings");
    if (settings) {
      try {
        const parsed = JSON.parse(settings);
        setCardStyle({
          width: `${parsed.width}px`,
          height: `${parsed.height}px`,
        });
      } catch (e) {
        console.error("Invalid cardSettings in localStorage", e);
      }
    }
  }, []);

  const configureFsc = () => {
    setShowConfig(true);
  };

  const saveSettings = () => {
    const defaultWidth = 1050;
    const defaultHeight = 660;

    let finalWidth = width;
    let finalHeight = height;

    if (!width || !height) {
      const confirmUseDefaults = window.confirm(
        "Width and Height should not be blank. Do you want to use default values?"
      );

      if (!confirmUseDefaults) {
        // User chose to cancel
        return;
      }

      finalWidth = width || defaultWidth;
      finalHeight = height || defaultHeight;
    }

    const settings = {
      width: finalWidth,
      height: finalHeight,
      format,
    };

    setSavedSettings(settings);
    setShowConfig(false);
    localStorage.setItem("cardSettings", JSON.stringify(settings));

    alert("Settings saved!");
  };

  const closeSettings = () => {
    setShowConfig(false);
  };

  // Open the EPDS site in a popup
  const openFscSearch = () => {
    localStorage.removeItem("fscData"); // Clear previous data
    window.open(
      "https://epds.telangana.gov.in/FoodSecurityAct/?wicket:bookmarkablePage=:nic.fsc.foodsecurity.FscSearch",
      "_blank"
    );
  };

  const handleFileUpload = async (event) => {
    // localStorage.removeItem("fscData");
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const buffer = new Uint8Array(e.target.result);
        const salt = buffer.slice(0, 16);
        const iv = buffer.slice(16, 28);
        const data = buffer.slice(28);

        const enc = new TextEncoder();
        const keyMaterial = await window.crypto.subtle.importKey(
          "raw",
          enc.encode("your-secret-password"), // Same passphrase as above
          { name: "PBKDF2" },
          false,
          ["deriveKey"]
        );

        const key = await window.crypto.subtle.deriveKey(
          {
            name: "PBKDF2",
            salt,
            iterations: 100000,
            hash: "SHA-256",
          },
          keyMaterial,
          { name: "AES-GCM", length: 256 },
          false,
          ["decrypt"]
        );

        const decrypted = await window.crypto.subtle.decrypt(
          { name: "AES-GCM", iv },
          key,
          data
        );

        const json = JSON.parse(new TextDecoder().decode(decrypted));
        if (json.rationCardDetails) {
          localStorage.setItem("fscData", JSON.stringify(json));
          setCardDetails(json.rationCardDetails);
          setMemberNames(json.memberNames);
        } else {
          throw new Error("Invalid FSC data format.");
        }
      } catch (err) {
        console.error("❌ Failed to decrypt or parse uploaded file:", err);
        alert("❌ Invalid or corrupted file.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDownload = async () => {
    const user = auth.currentUser;
    if (!user) return alert("User not logged in");

    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return alert("User not found");

    const userData = userSnap.data();
    if (userData.credits < 50) return alert("Insufficient credits");

    const fscData = JSON.parse(localStorage.getItem("fscData"));
    const cardNo = fscData?.rationCardDetails?.newRationCardNo;
    if (!cardNo) return alert("Card number not found");

    const alreadyDownloaded = (userData.downloads || []).some(
      (item) => item.cardNo === cardNo
    );

    const front = document.getElementById("fsc-front");
    const back = document.getElementById("fsc-back");

    if (alreadyDownloaded) {
      // Store data for re-download
      pendingDownloadData.current = { userRef, cardNo, front, back, userData };

      // Show confirmation modal
      const modal = new Modal(modalRef.current);
      modal.show();
      return;
    }

    // First-time download: deduct credits
    await performDownload(userRef, cardNo, userData, front, back, true);
  };

  const handleConfirmedDownload = async () => {
    const { userRef, cardNo, front, back, userData } =
      pendingDownloadData.current;

    // Re-download: do NOT deduct credits
    await performDownload(userRef, cardNo, userData, front, back, false);

    const modalInstance = Modal.getInstance(modalRef.current);
    modalInstance.hide();
  };

  const performDownload = async (
    userRef,
    cardNo,
    userData,
    front,
    back,
    deductCredits
  ) => {
    const settings = JSON.parse(localStorage.getItem("cardSettings")) || {
      width: "1050px",
      height: "660px",
      format: "png",
    };

    const widthMM = parseFloat(settings.width);
    const heightMM = parseFloat(settings.height);
    const format = settings.format;

    const downloadImage = async (element, suffix) => {
      const canvas = await html2canvas(element, { scale: 3 });
      const imgData = canvas.toDataURL(
        `image/${format === "jpeg" ? "jpeg" : "png"}`
      );
      const fileName = `${cardNo}_${suffix}`;

      if (format === "pdf") {
        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: [widthMM, heightMM],
        });
        pdf.addImage(imgData, "PNG", 0, 0, widthMM, heightMM);
        pdf.save(`${fileName}.pdf`);
      } else {
        const link = document.createElement("a");
        link.href = imgData;
        link.download = `${fileName}.${format}`;
        link.click();
      }
    };

    try {
      if (front) await downloadImage(front, "front");
      if (back) await downloadImage(back, "back");

      if (deductCredits) {
        await updateDoc(userRef, {
          credits: increment(-20),
          downloads: arrayUnion({
            cardNo,
            downloadedAt: new Date().toISOString(),
          }),
        });
      }
    } catch (err) {
      console.error("Download failed:", err);
      alert("Download failed. Please try again.");
    }
  };

  return (
    <div>
      {/* //header code */}
      <div
        style={{
          backgroundColor: "#6DC38F",
          width: "100%",
          height: "90px",
          borderRadius: "10px",
          marginBottom: "20px",
        }}
      >
        <img
          src={headerImg}
          alt="Logo"
          width="100%"
          //   style={{ width: "100%", height: "auto" }}
        ></img>{" "}
      </div>
      <div className="container mt-1">
        {" "}
        <div className="row">
          {/* Left Side - 8 Columns */}{" "}
          <div className="col-md-10 d-flex align-items-center justify-content-center">
            {" "}
            <button className="btn btn-success me-3" onClick={openFscSearch}>
              New Card{" "}
            </button>{" "}
            <input type="file" accept=".json" onChange={handleFileUpload} />{" "}
            <button
              className="ms-3 btn btn-success"
              onClick={() => handleDownload(downloadFormat)}
            >
              Download Card
            </button>
          </div>
          {/* Right Side - 4 Columns */}{" "}
          <div className="col-md-2 d-flex flex-column align-items-center justify-content-center">
            {" "}
            <button
              className="btn btn-sm btn-secondary d-flex align-items-center justify-content-center"
              onClick={configureFsc}
            >
              Configurer{" "}
            </button>{" "}
            {showConfig && (
              <div className="border p-3 rounded">
                {" "}
                <input
                  type="text"
                  placeholder="Width in mm"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  className="form-control mb-2"
                />{" "}
                <input
                  type="text"
                  placeholder="Height in mm"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  className="form-control mb-2"
                />
                <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                  Select Format:
                </label>{" "}
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  className="form-select mb-2"
                >
                  <option value="png">PNG</option>
                  <option value="jpeg">JPEG</option>
                  <option value="pdf">PDF</option>{" "}
                </select>{" "}
                <button
                  className="btn btn-sm btn-primary"
                  onClick={saveSettings}
                >
                  Save{" "}
                </button>{" "}
                <button
                  className="btn btn-sm btn-danger"
                  onClick={closeSettings}
                >
                  Cancel{" "}
                </button>{" "}
              </div>
            )}{" "}
            {savedSettings && (
              <div className="" style={{ fontSize: "11px" }}>
                <p>
                  W: {savedSettings.width}px, H: {savedSettings.height}px - [{" "}
                  {savedSettings.format.toUpperCase()}]
                  {/* <p>H: {savedSettings.height}mm </p> */}
                  {/* <p> Format: {savedSettings.format.toUpperCase()}</p> */}
                </p>
              </div>
            )}{" "}
          </div>{" "}
        </div>
        {/* FSC Search */}{" "}
      </div>
      {debugLog && (
        <div style={{ marginTop: "10px", color: "green" }}>
          <strong>{debugLog}</strong>
        </div>
      )}

      <div
        id="fsc-front"
        className="mt-4 p-3 border border-2 border-light"
        style={{
          ...cardStyle,
          position: "relative",
          userSelect: "none", // ❌ Prevent selection
          pointerEvents: "none", // ❌ Prevent right-click copy
        }}
      >
        {/* <Watermark /> */}
        <img
          src={headerImg}
          alt="Logo"
          style={{
            width: "100%",
            borderRadius: "5px",
          }}
        />
        <div
          className="d-flex justify-content-center align-items-center mt-1 mb-1 p-1 bg bg-success text-white border rounded"
          style={{
            fontSize: "4.2rem",
            color: "#fff",
            height: "80px",
          }}
        >
          <h2>Ration Card Details:</h2>
        </div>

        <div
          className="d-flex justify-content-center align-items-center mt-1 mb-1 p-2 border border-3 border-success rounded"
          style={{
            fontSize: "5.2rem",
            height: "80px",
            userSelect: "text", // ✅ Allow selection
            pointerEvents: "auto", // ✅ Allow interaction
          }}
        >
          <h2 className="text-primary">New Ration Card No:</h2>
          <h1 className="text-danger ms-5">
            {cardDetails?.newRationCardNo || "__________"}
          </h1>
        </div>

        <table
          style={{
            borderCollapse: "collapse",
            border: "2px solid #000",
            width: "100%",
            // height: "70px",
          }}
        >
          <tbody>
            {[
              ["Head of the Family :", "headOfFamily"],
              ["Card Type :", "cardType"],
              ["FSC Reference No :", "fscRefNo"],
              ["Application No :", "applicationNo"],
              ["Application Status :", "appStatus"],
              ["IMPDS Status :", "impdsStatus"],
              ["SKS Form No :", "sksFormNo"],
              ["FPShop No :", "fpShopNo"],
              ["KeyRegister Sl.No :", "keyRegisterSlNo"],
              ["Gas Connection :", "gasConnection"],
              ["Consumer No :", "consumerNo"],
              ["Office Name :", "officeName"],
              ["District :", "district"],
              ["Old RCNo :", "oldRCNo"],
              // ["__________ ", ""],
            ]
              .reduce((rows, item, index, array) => {
                if (index % 2 === 0) {
                  rows.push(array.slice(index, index + 2));
                }
                return rows;
              }, [])
              .map((rowTriplet, index) => (
                <tr
                  key={index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#D6F2A1" : "#63c9c9",

                    color: "#000",
                  }}
                >
                  {rowTriplet.map(([label, key], i) => (
                    <React.Fragment key={i}>
                      <td
                        style={{
                          width: "25%",
                          padding: "6px",
                          fontSize: "16px",
                          border: "2px solid #fff",
                          height: "50px",
                        }}
                      >
                        {label}
                      </td>
                      <td
                        style={{
                          padding: "6px",
                          width: "25%",
                          borderRight: i < 2 ? "1px solid #000" : "none",
                          fontWeight: "bold",
                          fontSize: "18px",
                          border: "2px solid #fff",
                        }}
                      >
                        {cardDetails?.[key] || "__________"}
                      </td>
                    </React.Fragment>
                  ))}
                  {rowTriplet.length < 2 &&
                    Array.from({ length: 2 - rowTriplet.length }).map(
                      (_, i) => (
                        <React.Fragment key={`empty-${i}`}>
                          <td></td>
                          <td></td>
                        </React.Fragment>
                      )
                    )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* ======================= */}
      {/* FAMILY MEMBERS DETAILS */}
      {/* ======================= */}

      <div
        id="fsc-back"
        className="mt-5 p-3 border border-2 border-light "
        style={{
          ...cardStyle,
          position: "relative",
          userSelect: "none", // ❌ Prevent selection
          pointerEvents: "none", // ❌ Prevent right-click copy
          // marginLeft: "16rem",
          // width: "1050px",
          // height: "660px",
          // margin: "0 auto",
        }}
      >
        {/* <Watermark /> */}
        <div className="d-flex justify-content-center align-items-center mb-2">
          <img
            src={headerImg}
            alt="Logo"
            style={{
              width: "100%",
              height: "auto",
              borderRadius: "5px",
            }}
          />
        </div>

        <h2
          className="text-white bg bg-success p-2 text-center border rounded"
          style={{ height: "80px" }}
        >
          Ration Card Family Member Details
        </h2>

        <table
          className="justify-content-center align-items-center"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            textAlign: "center",
          }}
        >
          <tbody>
            <tr
              style={{
                backgroundColor: "#63c9c9",
                color: "#fff",
              }}
            >
              <td
                style={{
                  padding: "8px",
                  fontWeight: "bold",
                  textShadow:
                    "1.5px 1.5px 0 #000, -1.5px -1.5px 0 #000, 1.5px -1.5px 0 #000, -1.5px 1.5px 0 #000",
                }}
              >
                S.No
              </td>
              <td
                style={{
                  padding: "8px",
                  fontWeight: "bold",
                  textShadow:
                    "1.5px 1.5px 0 #000, -1.5px -1.5px 0 #000, 1.5px -1.5px 0 #000, -1.5px 1.5px 0 #000",
                }}
              >
                Name
              </td>
              <td></td>
            </tr>
            {memberNames.length > 0 ? (
              memberNames.map((name, index) => (
                <tr
                  key={index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#D6F2A1" : "#63c9c9",
                  }}
                >
                  <td
                    style={{
                      padding: "8px",
                      fontWeight: "bold",
                      fontSize: "20px",
                      height: "50px",
                    }}
                  >
                    {index + 1}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      fontWeight: "bold",
                      fontSize: "20px",
                    }}
                  >
                    {name}
                  </td>
                  <td></td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="2" style={{ padding: "8px", fontStyle: "italic" }}>
                  No member data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div
          className="position-absolute bottom-0 start-0 end-0 text-center p-2"
          style={{
            backgroundColor: "rgb(4, 98, 108);",
            color: "#fff",
          }}
        >
          <small className="text-muted">
            <strong>Disclaimer:</strong> This digital card is for informational
            purposes only and does not replace the official government-issued
            document.
          </small>
        </div>
      </div>
      {/* Confirmation Modal */}
      <div
        className="modal fade"
        id="confirmDownloadModal"
        tabIndex="-1"
        aria-labelledby="confirmDownloadModalLabel"
        aria-hidden="true"
        ref={modalRef}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header bg bg-warning">
              <h5 className="modal-title" id="confirmDownloadModalLabel">
                Card Already Downloaded
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              This card was already downloaded. Do you want to download it
              again?
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                // id="confirmDownloadBtn"
                onClick={handleConfirmedDownload}
              >
                Yes, Download Again
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          margin: "5rem 0rem .5rem 0rem",
          fontSize: "12px",
          color: "#555",
          // width: "80%",
          backgroundColor: "#008080",
        }}
      >
        <p
          style={{
            margin: "0rem 3rem .5rem 3rem",
            color: "#fff",
          }}
        >
          Disclaimer: This information displayed above has been retrieved with
          the explicit consent of the user soley for the purpose of viewing and
          downloading the FSC card. This website does not store, share, or
          misuse any personal data. All data remains the proprty of the
          respective goverment authority and is used here only for user
          requested service. We are not affiliated with any government agency.
          User are advised to verify the accuracy of the information before
          proceeding.
        </p>
      </div>
    </div>
  );
};

export default FscCard;
